// KPI rail — a handful of headline numbers, so stat tiles rather than a chart.
// Exactly one hero figure per view: the pass rate. Values use proportional figures
// (tabular-nums makes a large number look loose); tabular is reserved for the tables.
//
// Numeric values count up on mount (useCountUp) — undershoot-only, lands exactly,
// disabled under prefers-reduced-motion. Text values (a pod name) render as-is.
import { pct, signedPts, compact } from '../lib/format';
import { useCountUp } from '../lib/useCountUp';

function Value({ num, fmt, text }) {
  const animated = useCountUp(num);
  if (num === null || num === undefined) return <>{text ?? '—'}</>;
  return <>{fmt(animated)}</>;
}

function Tile({ label, hero = false, num = null, fmt = compact, text = null, delta = null, deltaGood = null, foot = null }) {
  return (
    <div className={`tile${hero ? ' tile--hero' : ''}`}>
      <p className="tile__label">{label}</p>
      <p className="tile__value"><Value num={num} fmt={fmt} text={text} /></p>
      {delta !== null && (
        <p className={`tile__delta${deltaGood === null ? '' : deltaGood ? ' is-good' : ' is-bad'}`}>
          {delta}
        </p>
      )}
      {foot && <p className="tile__foot">{foot}</p>}
    </div>
  );
}

export default function KpiRail({ summary }) {
  const p = summary.pass_rate;
  const top = summary.top_pod_at_risk;
  const hasDelta = p.delta_pts !== null && p.delta_pts !== undefined;

  return (
    <div className="kpi-rail">
      <Tile
        hero
        label="Pass rate"
        num={p.current}
        fmt={pct}
        delta={hasDelta ? `${signedPts(p.delta_pts)} vs last week` : 'no comparable run last week'}
        deltaGood={hasDelta ? p.delta_pts >= 0 : null}
        foot={`${compact(p.passed)} of ${compact(p.executed)} executed · ${compact(p.skipped)} skipped`}
      />
      <Tile
        label="Open signals"
        num={summary.open_signals.total}
        fmt={(v) => compact(Math.round(v))}
        foot={`${summary.open_signals.high_blast} high-blast · ${summary.open_signals.bug_candidates} probable bug`}
      />
      <Tile
        label="Tests blocked"
        num={summary.tests_blocked}
        fmt={(v) => compact(Math.round(v))}
        foot="fixing every signal clears these"
      />
      <Tile
        label="Top pod at risk"
        text={top ? top.owner_pod || 'unowned' : '—'}
        foot={top ? `${top.tests_blocked} tests · impact ${top.top_impact}` : 'no owned signals'}
      />
      <Tile
        label="Unowned signals"
        num={summary.open_signals.unowned}
        fmt={(v) => compact(Math.round(v))}
        foot={summary.open_signals.unowned ? 'ownership unresolved — never guessed' : 'every signal is owned'}
      />
    </div>
  );
}
